import { useSearchParams } from 'react-router-dom';
import type { SiteExplorerTabs } from 'src/shared/types/siteExplorerTabs';

/** Selects the tab. */
export const VIEW_PARAM = 'view';

/** Frames the map on one site. Named for what it does rather than for the site it
 *  names: a list of sites has no one site, and only the map view reads it. */
export const FOCUS_SITE_PARAM = 'focusSite';

/** Params that choose what the user sees, not which sites they see. `useGnrFilters`
 *  reads only its own site filter names, so these are never filters. */
export const VIEW_PARAMS: ReadonlySet<string> = new Set([VIEW_PARAM, FOCUS_SITE_PARAM]);

/** Every tab the explorer can show. A Record rather than a list, so a new member of
 *  `MapOrTable` fails to compile until it is named here. */
const VIEWS: Record<SiteExplorerTabs, true> = { map: true, list: true };

/** The tab a URL with no `view` shows. `site-list` is the route that existed before
 *  the tabs, so a bare link still opens the list. */
const DEFAULT_VIEW: SiteExplorerTabs = 'list';

const isView = (value: string | null): value is SiteExplorerTabs => value !== null && value in VIEWS;

/** The tab the URL names. A missing or unknown value gives the default. */
export const viewFromParams = (params: URLSearchParams): SiteExplorerTabs => {
    const named = params.get(VIEW_PARAM);
    return isView(named) ? named : DEFAULT_VIEW;
};

export type SiteExplorerView = {
    view: SiteExplorerTabs;
    setView: (next: SiteExplorerTabs) => void;
    /** The site the map frames on, when the URL names one. */
    focusSiteId?: string;
};

/**
 * The view state of the Site Explorer, held in the URL.
 *
 * In the URL rather than in state, so a view survives a refresh and the user can
 * send it to someone.
 */
const useSiteExplorerView = (): SiteExplorerView => {
    const [searchParams, setSearchParams] = useSearchParams();

    const setView = (next: SiteExplorerTabs) => {
        const params = new URLSearchParams(searchParams);
        params.set(VIEW_PARAM, next);
        setSearchParams(params, { replace: true });
    };

    return {
        view: viewFromParams(searchParams),
        setView,
        focusSiteId: searchParams.get(FOCUS_SITE_PARAM) ?? undefined,
    };
};

export default useSiteExplorerView;
